import { useState, useEffect, useRef, useCallback } from 'react'
import { observer } from 'mobx-react-lite'

// evertel
import { Button, Text, Modal, Col, Row, InfoBox, ModalHeader, ModalBody, useUI, Input } from '@evertel/web/ui'
import { useService } from '@evertel/di'
import { AuthenticationController } from '@evertel/shared/feature-authentication-auth0'
import { Analytics } from '@evertel/analytics'
import { SessionService } from '@evertel/session/SessionService'
import { DeviceTokenService } from '@evertel/session/DeviceTokenService'

interface Props {
    isOpen: boolean
    onClose: () => void
    onComplete?: () => void
    phoneNumber?: string | number
}

const Confirm2FAEnrollmentModal: React.FC<Props> = observer(({
    isOpen = false,
    onClose,
    onComplete,
    phoneNumber
}) => {
    const analytics = useService(Analytics)
    const deviceTokenService = useService(DeviceTokenService)
    const authenticationController = useService(AuthenticationController)
    const sessionService = useService(SessionService)

    const autoSubmitCountRef = useRef(0)

    const { addToast } = useUI()

    const [code, setCode] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState('')

    useEffect(() => {
        if (isOpen) {
            // Reset state
            setCode('')
            setError('')
            setIsLoading(false)
            autoSubmitCountRef.current = 0

            analytics.logView('/account/security/confirm-enrollment', true)
        }
    }, [isOpen, analytics])

    // Auto-submit when 6 digits entered
    useEffect(() => {
        if (code?.length === 6 && autoSubmitCountRef.current < 1) {
            autoSubmitCountRef.current++
            submitVerifyCode()
        }
    }, [code])

    const onChangeCode = (value: string) => {
        if (!isLoading) {
            setCode(value.replace(/\D/g, ''))
        }
    }

    const submitVerifyCode = useCallback(async () => {
        setError('')

        if (!code) {
            setError('Please enter your security code')
            return
        }

        const mfaToken = authenticationController.mfaToken
        const oobCode = authenticationController.oobCode

        if (!mfaToken || !oobCode) {
            setError('Missing tokens')
            return
        }

        setIsLoading(true)

        try {
            const params = {
                mfaToken,
                oobCode,
                bindingCode: code,
                deviceToken: deviceTokenService.token || ''
            }

            await authenticationController.verifyChallenge(params)

            addToast({
                message: 'Enrollment Successful',
                color: 'success'
            })

            // Refresh authenticators list
            const userId = sessionService.currentUserId
            if (userId) {
                await authenticationController.getAuthenticators(userId)
            }

            onComplete?.()
            onClose()
        } catch (err: any) {
            setError(err.message)
        } finally {
            setIsLoading(false)
        }
    }, [code, authenticationController, deviceTokenService, sessionService, onComplete, onClose, addToast])

    return (
        <Modal
            visible={isOpen}
            className="join-dept-modal"
            onClose={onClose}
            backdrop="static"
            keyboard={false}
            size="md"
        >
            <ModalHeader title="Confirm SMS 2FA Enrollment" />
            <ModalBody>
                <Row>
                    <Col>
                        {error && (
                            <InfoBox color="danger">
                                {error}
                            </InfoBox>
                        )}

                        <Text className="pb-4">
                            A text message with a six-digit security code has been sent to your phone
                            number ending in <span className="text-primary">******{String(phoneNumber).slice(-4)}</span>
                        </Text>
                        <div className="" style={{maxWidth: 200, margin: 'auto'}}>
                            <h3 className="text-center">Security Code</h3>
                            <Input
                                className="two-factor-input text-center"
                                style={{
                                    letterSpacing: 10,
                                    fontSize: 'x-large',
                                    fontWeight: 'bold'
                                }}
                                name="securityCode"
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                value={code}
                                onChange={(e) => onChangeCode(e.target.value)}
                                maxLength={6}
                                placeholder="••••••"
                                aria-description="Two-Factor Code"
                                onKeyDown={(e) => {
                                    if (
                                        !/[\d]/.test(e.key)
                                        && !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'].includes(e.key)
                                        && !e.ctrlKey
                                        && !e.metaKey
                                    ) {
                                        e.preventDefault()
                                    }
                                }}
                            />
                        </div>
                    </Col>
                </Row>
                <Row>
                    <Col
                        align="center"
                        className="mt-4">
                        <Button
                            color="primary"
                            loading={isLoading}
                            disabled={isLoading}
                            onClick={submitVerifyCode}
                        >
                            <Text>Verify Security Code</Text>
                        </Button>
                    </Col>
                </Row>
            </ModalBody>
        </Modal>
    )
})

export { Confirm2FAEnrollmentModal }
